import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { dhfApi } from '../../utils/dhfApi';

/** Health colour: the score bands on the health scale (problem < 25 = Low ⇒ health 76–100). */
export function healthColor(h) {
  if (h === null || h === undefined) return '#A8A1BE';
  if (h > 75) return '#1E7D34';
  if (h > 50) return '#B45309';
  if (h > 25) return '#C2410C';
  return '#C5221F';
}

/** A 0–100 health ring. `health` null = not scored (grey, "—"). */
export function ScoreRing({ health, size = 52, label = 'health' }) {
  const stroke = size > 80 ? 9 : 5;
  const r = (size - stroke) / 2 - 1;
  const c = 2 * Math.PI * r;
  const on = health === null || health === undefined ? 0 : Math.max(0, Math.min(100, health));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="dhf-ring"
         role="img" aria-label={health == null ? 'Not scored' : `Health ${health} of 100`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ECE9F3" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={healthColor(health)} strokeWidth={stroke}
              strokeLinecap="round" strokeDasharray={`${(c * on) / 100} ${c}`}
              transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y={size > 80 ? '52%' : '50%'} dominantBaseline="middle" textAnchor="middle"
            className={size > 80 ? 'dhf-ring-n dhf-ring-n--big' : 'dhf-ring-n'}>
        {health == null ? '—' : health}
      </text>
      {size > 80 && <text x="50%" y="72%" textAnchor="middle" className="dhf-ring-l">{label}</text>}
    </svg>
  );
}

export const FINDING_TONE = {
  gap: 'bad', failed: 'bad', not_done: 'warn', not_verified: 'warn', ok: 'good',
};

export function Chip({ tone = 'neutral', children, title }) {
  return <span className={`dhf-chip dhf-chip--${tone}`} title={title}>{children}</span>;
}

export function doneChip(fact, status) {
  if (!status && (fact?.done === null || fact?.done === undefined)) return null;
  const tone = fact?.done === true ? 'good' : fact?.done === false ? 'warn' : 'neutral';
  return <Chip tone={tone} title={fact?.done === true ? 'Active and complete' : fact?.done === false ? 'Not complete yet' : 'Status not in the status table'}>{status || '—'}</Chip>;
}

export function coverageChip(f) {
  if (!f || !f.expected) return null;
  if (f.gap) return <Chip tone="bad">Gap · no {f.next_level || 'child'}</Chip>;
  return <Chip tone="good">{f.children} {f.next_level || 'children'}</Chip>;
}

export function testsChip(t) {
  if (!t || !(t.pass + t.fail + t.notrun)) return null;
  if (t.fail) return <Chip tone="bad">Failed {t.fail}/{t.pass + t.fail + t.notrun}</Chip>;
  if (t.notrun) return <Chip tone="neutral">Not run {t.notrun}/{t.pass + t.notrun}</Chip>;
  return <Chip tone="good">Passed {t.pass}/{t.pass}</Chip>;
}

/** Stamps are UTC (Critical Note #50) and say so. */
export function utc(stamp) {
  return stamp ? `${String(stamp).replace('T', ' ').slice(0, 16)} UTC` : '';
}

/**
 * Orcanos description HTML, made safe. An embedded-filter anchor (`<a name="FILTER_587">`) is
 * a table Orcanos renders in its own viewer — shown as a marker, never flattened into prose
 * (skill get-children.md, trap 3).
 */
export function safeHtml(html) {
  const marked = String(html || '').replace(
    /<a\b[^>]*name=["']FILTER_\d+["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_m, cap) => `<span class="dhf-embed">Embedded table: <b>${cap}</b> — shown in Orcanos</span>`);
  return DOMPurify.sanitize(marked, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'em', 'strong', 'span', 'br', 'p', 'ul', 'ol', 'li', 'a', 'table', 'thead',
                   'tbody', 'tr', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'sub', 'sup', 'div'],
    ALLOWED_ATTR: ['href', 'class', 'colspan', 'rowspan'],
  });
}

export function isEmptyHtml(html) {
  return !String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * The trace panel's build (one build serves Funnel, Dashboard and DHF), polled on the shared
 * export-job route. The poll is cleared on unmount (Critical Note #39: the POLL dies, the job lives).
 */
export function useDhfBuild(setupId, onDone) {
  const [state, setState] = useState({ running: false, progress: 0, message: '', error: '' });
  const timer = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => () => clearInterval(timer.current), []);

  const start = async (smart = true) => {
    setState({ running: true, progress: 0, message: 'Starting…', error: '' });
    try {
      const job = await dhfApi.build(setupId, smart);
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const j = await dhfApi.job(job.job_id);
          if (j.status === 'completed') {
            clearInterval(timer.current);
            setState({ running: false, progress: 100, message: '', error: '' });
            if (doneRef.current) doneRef.current();
          } else if (j.status === 'error') {
            clearInterval(timer.current);
            setState({ running: false, progress: 0, message: '', error: j.error || 'The build failed' });
          } else {
            setState((s) => ({ ...s, progress: j.progress || 0, message: j.message || s.message }));
          }
        } catch (e) {
          clearInterval(timer.current);
          setState({ running: false, progress: 0, message: '', error: e.message });
        }
      }, 900);
    } catch (e) {
      setState({ running: false, progress: 0, message: '', error: e.message });
    }
  };
  return { ...state, start };
}
